import React, { useCallback } from "react";
import { Box, Typography, Paper, Button, useTheme } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import VisibilityIcon from "@mui/icons-material/Visibility";
import sh from "bundles/common/utils/sh";
import { replaceCurrentRecruiter } from "bundles/Recruiter/constants/recruiterConstants";

const CvTextExcerptBlock = ({ candidate }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const authenticityToken = useSelector((state) => state.authenticity_token);
  const currentRecruiter = useSelector((state) => state.current_recruiter);
  const excerpt = candidate?.cv_text_excerpt;
  const candidateId = candidate?.id;
  const isGodview = !!currentRecruiter?.godview;
  const isCandidateRevealed =
    isGodview ||
    (!!candidateId &&
      currentRecruiter?.candidate_interests?.some(
        (interest) => interest.candidate_id === candidateId
      ));

  const revealCandidate = useCallback(async () => {
    if (!candidateId || isCandidateRevealed) return;

    try {
      await sh.post("/recruiter/reveal_candidate", {
        authenticity_token: authenticityToken,
        candidate_id: candidateId,
      });
      replaceCurrentRecruiter(dispatch, authenticityToken, () => {});
    } catch (err) {
      console.error(err);
      enqueueSnackbar(
        intl.formatMessage({
          id: "flash.error.generic",
          defaultMessage: "Failed to reveal candidate",
        }),
        { variant: "error" }
      );
    }
  }, [
    candidateId,
    isCandidateRevealed,
    authenticityToken,
    dispatch,
    enqueueSnackbar,
    intl,
  ]);

  const handleSeeCvClick = () => {
    if (!candidate?.resume_url) return;
    if (!isCandidateRevealed) revealCandidate();
    window.open(candidate.resume_url, "_blank", "noopener,noreferrer");
  };

  if (!excerpt || !excerpt.match) return null;

  return (
    <Paper sx={{ padding: "20px", marginTop: "20px", boxShadow: "none", borderRadius: "10px" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
        <Typography variant="h4" fontWeight={600} sx={{ color: theme.palette.secondary.black }}>
          <FormattedMessage id="recruiter.cv_theque.cv_text_match" defaultMessage="CV text" />
        </Typography>
        {candidate?.resume_url && (
          <Button
            startIcon={<VisibilityIcon />}
            type="button"
            onClick={handleSeeCvClick}
            size="small"
            sx={{
              padding: "6px 24px",
              borderRadius: "29px",
              color: theme.palette.text.main,
              border: `1px solid ${theme.palette.secondary.grey_strong}`,
              flexShrink: 0,
            }}
          >
            <Typography variant="h4" sx={{ fontSize: "14px !important", fontFamily: "Segoe UI Semibold, system-ui, sans-serif", fontWeight: "800" }}>
              <FormattedMessage id="recruiter.cv_theque.see_cv" defaultMessage="See CV" />
            </Typography>
          </Button>
        )}
      </Box>
      <Typography
        sx={{
          mt: "12px",
          fontSize: "12px",
          lineHeight: 1.55,
          fontWeight: 400,
          color: theme.palette.secondary.black,
          letterSpacing: "0.02em",
          fontFamily: "Segoe UI Regular, sans-serif",
        }}
      >
        {excerpt.truncated_start && "… "}
        {excerpt.before}
        {excerpt.before && " "}
        <Box component="span" sx={{ fontWeight: 700, textDecoration: "underline" }}>
          {excerpt.match}
        </Box>
        {excerpt.after && " "}
        {excerpt.after}
        {excerpt.truncated_end && " …"}
      </Typography>
    </Paper>
  );
};

export default CvTextExcerptBlock;
